import { useMemo, useState } from 'react'
import { BarChart3, ChevronDown, ChevronUp, Maximize2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEventPlannerStore, useEventStats } from '../store/eventPlannerStore'
import {
  concurrencyHistogram,
  estimateZoneCapacity,
  peakConcurrency,
} from '../engine/capacity'
import { formatShort } from '../data/formatTime'
import { formatUsd } from './charts'

const W = 280
const H = 54

/**
 * Capacity dashboard — the "time, not space" view. Shows total slot capacity vs
 * placed, and a peak-concurrency sparkline over the event window (the real
 * ceiling). Read-only; pure-derived from the store.
 */
export function CapacityPanel({ onExpand }: { onExpand?: () => void }) {
  const [open, setOpen] = useState(true)
  const zones = useEventPlannerStore((s) => s.zones)
  const placements = useEventPlannerStore((s) => s.placements)
  const period = useEventPlannerStore((s) => s.statsPeriod)
  const windowStart = period.start
  const windowEnd = period.end
  const currentTime = useEventPlannerStore((s) => s.currentTime)
  const seek = useEventPlannerStore((s) => s.seek)
  const capturedRevenue = useEventStats().captured.total

  const totalCapacity = useMemo(
    () => zones.reduce((a, z) => a + estimateZoneCapacity(z), 0),
    [zones]
  )

  const placedList = useMemo(() => Object.values(placements), [placements])
  const placed = placedList.length

  const hist = useMemo(
    () => concurrencyHistogram(placedList, windowStart, windowEnd, 240),
    [placedList, windowStart, windowEnd]
  )
  const peak = useMemo(() => peakConcurrency(hist), [hist])

  const saturatedZones = useMemo(() => {
    const byZone = new Map<string, number>()
    for (const p of placedList) byZone.set(p.areaId, (byZone.get(p.areaId) ?? 0) + 1)
    return zones.filter((z) => {
      const cap = estimateZoneCapacity(z)
      return cap > 0 && (byZone.get(z.areaId) ?? 0) >= cap * 0.95
    }).length
  }, [placedList, zones])

  // Sparkline geometry
  const yMax = Math.max(totalCapacity, peak.count, 1)
  const path = useMemo(() => {
    if (hist.length === 0) return ''
    const pts = hist.map((p, i) => {
      const x = (i / (hist.length - 1)) * W
      const y = H - (p.count / yMax) * H
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    return `M0,${H} L${pts.join(' L')} L${W},${H} Z`
  }, [hist, yMax])

  const capacityY = H - (totalCapacity / yMax) * H
  const nowX = ((currentTime - windowStart) / (windowEnd - windowStart)) * W
  const capacityPct = totalCapacity > 0 ? Math.round((placed / totalCapacity) * 100) : 0

  const onSparkClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const frac = (e.clientX - rect.left) / rect.width
    seek(windowStart + frac * (windowEnd - windowStart))
  }

  return (
    <div className="pointer-events-auto absolute left-4 top-[72px] w-[312px] rounded-xl border border-white/10 bg-slate-950/85 backdrop-blur-md">
      <div className="flex w-full items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-xs font-semibold text-white"
        >
          <BarChart3 className="h-3.5 w-3.5 text-sky-300" /> Capacity
        </button>
        <div className="flex items-center gap-2 text-[11px] text-white/50">
          <span className="tabular-nums">
            {placed}/{totalCapacity} · {capacityPct}%
          </span>
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              title="Open full statistics"
              className="rounded p-0.5 text-white/50 transition-colors hover:bg-white/10 hover:text-sky-200"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-white/50 hover:text-white">
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="space-y-2 px-3 pb-3">
          {/* Peak-concurrency sparkline */}
          <div>
            <div className="mb-1 flex items-center justify-between text-[10px] text-white/40">
              <span>On field over time</span>
              <span className="text-amber-300">
                peak {peak.count} · {formatShort(peak.t)}
              </span>
            </div>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="h-[54px] w-full cursor-pointer"
              onClick={onSparkClick}
              preserveAspectRatio="none"
            >
              <path d={path} fill="rgba(56,189,248,0.25)" stroke="rgb(56,189,248)" strokeWidth={1} />
              {/* capacity ceiling */}
              <line
                x1={0}
                y1={capacityY}
                x2={W}
                y2={capacityY}
                stroke="rgba(248,113,113,0.7)"
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              {/* now marker */}
              <line x1={nowX} y1={0} x2={nowX} y2={H} stroke="white" strokeWidth={1} opacity={0.6} />
            </svg>
            <div className="flex items-center justify-between text-[9px] text-white/30">
              <span>{formatShort(windowStart)}</span>
              <span className="text-red-300/70">— capacity {totalCapacity}</span>
              <span>{formatShort(windowEnd)}</span>
            </div>
          </div>

          {/* Insight line */}
          <div
            className={cn(
              'rounded-md px-2 py-1 text-[10px]',
              peak.count <= totalCapacity
                ? 'bg-emerald-500/10 text-emerald-300'
                : 'bg-red-500/10 text-red-300'
            )}
          >
            {peak.count <= totalCapacity
              ? `Peak demand ${peak.count} fits within ${totalCapacity} slots — the constraint is time, not space.`
              : `Peak demand ${peak.count} exceeds ${totalCapacity} slots — overflow at peak.`}
          </div>

          <div className="flex items-center justify-between text-[10px] text-white/40">
            <span>{saturatedZones}/{zones.length} zones ≥95% full</span>
            <span className="text-emerald-300/80">{formatUsd(capturedRevenue, true)} captured</span>
          </div>
        </div>
      )}
    </div>
  )
}
